import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { calendarService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { ICalendarLockConfig, ICalendarLockResponse } from "@miplatform/types";
import { useSession } from "@mibuildweb/hooks";
import {
  LOCK_RETRY_DELAY_MS,
  getIsLockedByOtherUser,
  getLockRenewalDelayMs,
} from "./calendarLock.utils";

const lockRefreshInterval = 5 * 1000; // 5 seconds
const CalendarLockContext = createContext<CalendarLockContextType | undefined>(
  undefined
);

export const CalendarLockProvider: React.FC<CalendarLockProviderProps> = ({
  children,
}) => {
  const { params } = useRouter();
  const { calendarSetId } = params;
  const { session } = useSession();
  const [isCalendarLocked, setIsCalendarLocked] = useState(false);
  const [lockDetails, setLockDetails] = useState<ICalendarLockConfig | null>(
    null
  );
  const [lockAcquiredState, setLockAcquiredState] =
    useState<ICalendarLockResponse | null>(null);
  const acquireInFlightRef =
    useRef<Promise<ICalendarLockResponse | null> | null>(null);

  const getCalendarSetLockStatus = async () => {
    if (!calendarSetId) {
      return;
    }
    const response = await calendarService.calendarSets.getLockStatus({
      id: calendarSetId,
    });
    if (response.status === HTTP_STATUSES.OK) {
      setIsCalendarLocked(response.data.isLocked);
      if (response.data.isLocked) {
        setLockDetails({
          lockedByUserId: response.data.lockedByUserId,
          lockedByUserNameDisplayName: response.data.lockedByUserDisplayName,
          lockedUntil: response.data.lockedUntil,
          message: response.data.message,
        });
      } else {
        setLockDetails(null);
      }
    }
  };

  useEffect(() => {
    getCalendarSetLockStatus();
    const intervalId = setInterval(() => {
      getCalendarSetLockStatus();
    }, lockRefreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const acquireCalendarLock = useCallback(
    async (onLockFailure?: () => void) => {
      if (!calendarSetId) {
        return null;
      }

      if (acquireInFlightRef.current) {
        return acquireInFlightRef.current;
      }

      const acquirePromise = (async () => {
        try {
          const response = await calendarService.calendarSets.acquireLock({
            id: calendarSetId,
          });
          if (response.status === HTTP_STATUSES.OK) {
            setLockAcquiredState(response.data);
            if (response.data.lockAcquired) {
              setIsCalendarLocked(false);
              setLockDetails(null);
            } else {
              setIsCalendarLocked(true);
              toastService.error({ text: response.data.message });
              onLockFailure?.();
              setLockDetails({
                lockedByUserId: response.data.lockedByUserId,
                lockedByUserNameDisplayName:
                  response.data.lockedByUserDisplayName,
                lockedUntil: response.data.lockedUntil,
                message: response.data.message,
              });
            }
            return response.data;
          }

          return null;
        } finally {
          acquireInFlightRef.current = null;
        }
      })();

      acquireInFlightRef.current = acquirePromise;
      return acquirePromise;
    },
    [calendarSetId]
  );

  const createCalendarLockRenewal = useCallback(
    (onLockFailure?: () => void, initialLockedUntil?: string | null) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      let isCancelled = false;

      const scheduleNextRenewal = (delayMs: number) => {
        timeoutId = setTimeout(() => {
          renewLock().catch(() => {
            if (!isCancelled) {
              scheduleNextRenewal(LOCK_RETRY_DELAY_MS);
            }
          });
        }, delayMs);
      };

      const renewLock = async () => {
        if (isCancelled) {
          return;
        }

        const lockResponse = await acquireCalendarLock(onLockFailure);
        if (isCancelled) {
          return;
        }

        if (lockResponse && !lockResponse.lockAcquired) {
          return;
        }

        scheduleNextRenewal(getLockRenewalDelayMs(lockResponse?.lockedUntil));
      };

      if (initialLockedUntil) {
        scheduleNextRenewal(getLockRenewalDelayMs(initialLockedUntil));
      } else {
        renewLock().catch(() => {
          if (!isCancelled) {
            scheduleNextRenewal(LOCK_RETRY_DELAY_MS);
          }
        });
      }

      return () => {
        isCancelled = true;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        setLockAcquiredState(null);
      };
    },
    [acquireCalendarLock]
  );

  const isLockedByOtherUser = useMemo(
    () =>
      getIsLockedByOtherUser({
        isCalendarLocked,
        lockAcquired: lockAcquiredState?.lockAcquired,
        sessionUserId: session?.userId,
        lockedByUserId: lockDetails?.lockedByUserId,
      }),
    [
      isCalendarLocked,
      lockAcquiredState?.lockAcquired,
      lockDetails?.lockedByUserId,
      session?.userId,
    ]
  );

  const clearLockHolderState = useCallback(() => {
    setLockAcquiredState(null);
  }, []);

  const calendarLockValue = useMemo(
    () => ({
      isCalendarLocked,
      isLockedByOtherUser,
      lockDetails,
      lockAcquiredState,
      getCalendarSetLockStatus,
      acquireCalendarLock,
      createCalendarLockRenewal,
      clearLockHolderState,
    }),
    [
      isCalendarLocked,
      isLockedByOtherUser,
      lockDetails,
      lockAcquiredState,
      acquireCalendarLock,
      createCalendarLockRenewal,
      clearLockHolderState,
    ]
  );

  return (
    <CalendarLockContext.Provider value={calendarLockValue}>
      {children}
    </CalendarLockContext.Provider>
  );
};

export const useCalendarLockContext = (): CalendarLockContextType => {
  const context = useContext(CalendarLockContext);
  if (context === undefined) {
    throw new Error(
      "useCalendarLockContext must be used within CalendarLockProvider"
    );
  }
  return context;
};

interface CalendarLockContextType {
  isCalendarLocked: boolean;
  isLockedByOtherUser: boolean;
  lockDetails: ICalendarLockConfig | null;
  lockAcquiredState: ICalendarLockResponse | null;
  getCalendarSetLockStatus: () => void;
  acquireCalendarLock: (
    onLockFailure?: () => void
  ) => Promise<ICalendarLockResponse | null>;
  createCalendarLockRenewal: (
    onLockFailure?: () => void,
    initialLockedUntil?: string | null
  ) => () => void;
  clearLockHolderState: () => void;
}

interface CalendarLockProviderProps {
  children: ReactNode;
}
